/**
 * Shared hook for checking if AI summaries are enabled
 * 
 * Use this hook to conditionally show/hide AI-related features throughout the app.
 * When AI is disabled, AI features should be hidden (not just show "disabled" messages).
 * 
 * Exception: Admin Settings AI tab should always be accessible (so admins can enable AI).
 * 
 * Behavior:
 * - While loading: isAiEnabled is undefined (check isLoading to show skeleton)
 * - On error: defaults to true (fail open - don't hide AI due to fetch issues)
 * - When loaded: returns actual value from API
 * 
 * Usage:
 *   const { isAiEnabled, isLoading } = useAiEnabled();
 *   if (isLoading) return <Skeleton />; // Show skeleton while loading
 *   if (!isAiEnabled) return null; // Hide when confirmed disabled
 */

import { useQuery } from "@tanstack/react-query";

interface AiSettings {
  aiSummariesEnabled: boolean;
  openaiKeyConfigured: boolean;
  model: string;
}

interface UseAiEnabledResult {
  isAiEnabled: boolean;
  isLoading: boolean;
  isError: boolean;
}

export function useAiEnabled(): UseAiEnabledResult {
  const { data, isLoading, isError } = useQuery<AiSettings>({
    queryKey: ["/api/settings/ai"],
    staleTime: 30000, // Cache for 30 seconds to avoid repeated fetches
  });

  // On error, fail open (show AI content)
  // While loading, return false but callers should check isLoading first
  const isAiEnabled = isError ? true : (data?.aiSummariesEnabled ?? false);

  return {
    isAiEnabled,
    isLoading,
    isError,
  };
}
